import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { getDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Building2, User, RefreshCw, Trash2, Upload, Plus } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { WebsiteSettings, resetWebsiteSettings } from '@/lib/firebase';

const IMGBB_API_KEY = '3a8ea70d9f504860c10ae7c657d486a8';
const IMGBB_API_URL = 'https://api.imgbb.com/1/upload';

const Settings = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState<WebsiteSettings>({
    doctor: {
      name: '',
      title: '',
      image: '',
      about: '',
      qualifications: ['', '', '', '', '', '', '', ''],
      statistics: {
        experienceYears: 0,
        successfulCases: 0,
        satisfactionRate: 0
      }
    },
    clinic: {
      name: 'دنتال جلو',
      logo: '/logo.svg',
      headerImage: '/header-placeholder.jpg',
      pageTitle: 'دنتال جلو | عيادة طب الأسنان',
      phone: ['+20 123 456 7890'],
      address: ['شارع النيل، المهندسين، الجيزة، مصر'],
      workingHours: ['السبت - الخميس: 9:00 ص - 9:00 م'],
      clinicPhotos: [],
      images: [],
      socialMedia: {
        facebook: 'https://facebook.com/dentalglow',
        instagram: 'https://instagram.com/dentalglow',
        twitter: 'https://twitter.com/dentalglow',
        whatsapp: '+201234567890'
      },
      contactInfo: {
        email: 'info@dentalglow.com',
        whatsapp: '+20 123 456 7890',
        mapEmbed: '<iframe src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1709.12970219841!2d30.84281233229951!3d31.046877317892314!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14f64d78fba93181%3A0xc8a9822d65f3544a!2z2YXYutiz2YTZhyDYr9in2YrZhQ!5e0!3m2!1sar!2seg!4v1749654081800!5m2!1sar!2seg" width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>'
      },
      location: {
        mapEmbed: '<iframe src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1709.12970219841!2d30.84281233229951!3d31.046877317892314!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14f64d78fba93181%3A0xc8a9822d65f3544a!2z2YXYutiz2YTZhyDYr9in2YrZhQ!5e0!3m2!1sar!2seg!4v1749654081800!5m2!1sar!2seg" width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>'
      }
    },
    qualifications: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'settings', 'website');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data() as WebsiteSettings;
        setSettings({
          doctor: {
            ...data.doctor,
            qualifications: Array.isArray(data.doctor?.qualifications) 
              ? data.doctor.qualifications 
              : ['', '', '', '', '', '', '', ''],
            statistics: data.doctor.statistics || {
              experienceYears: 0,
              successfulCases: 0,
              satisfactionRate: 0
            }
          },
          clinic: {
            ...data.clinic,
            phone: Array.isArray(data.clinic?.phone) ? data.clinic.phone : [''],
            address: Array.isArray(data.clinic?.address) ? data.clinic.address : [''],
            workingHours: Array.isArray(data.clinic?.workingHours) ? data.clinic.workingHours : [''],
            clinicPhotos: Array.isArray(data.clinic?.clinicPhotos) ? data.clinic.clinicPhotos : [],
            images: Array.isArray(data.clinic?.images) ? data.clinic.images : [],
            socialMedia: {
              facebook: data.clinic?.socialMedia?.facebook || '',
              instagram: data.clinic?.socialMedia?.instagram || '',
              twitter: data.clinic?.socialMedia?.twitter || '',
              whatsapp: data.clinic?.socialMedia?.whatsapp || ''
            },
            contactInfo: {
              email: data.clinic?.contactInfo?.email || '',
              whatsapp: data.clinic?.contactInfo?.whatsapp || '',
              mapEmbed: data.clinic?.contactInfo?.mapEmbed || ''
            },
            location: {
              mapEmbed: data.clinic?.location?.mapEmbed || ''
            }
          },
          qualifications: data.qualifications || []
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        variant: 'destructive',
        title: 'خطأ في تحميل الإعدادات',
        description: 'حدث خطأ أثناء تحميل الإعدادات، يرجى المحاولة مرة أخرى'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Create a clean settings object with all required fields
      const cleanSettings: WebsiteSettings = {
        doctor: {
          name: settings.doctor?.name?.trim() || '',
          title: settings.doctor?.title?.trim() || '',
          image: settings.doctor?.image?.trim() || '',
          about: settings.doctor?.about?.trim() || '',
          qualifications: (settings.doctor?.qualifications || []).map(q => q?.trim() || ''),
          statistics: settings.doctor?.statistics || {
            experienceYears: 0,
            successfulCases: 0,
            satisfactionRate: 0
          }
        },
        clinic: {
          name: settings.clinic?.name?.trim() || '',
          logo: settings.clinic?.logo?.trim() || '',
          headerImage: settings.clinic?.headerImage?.trim() || '',
          pageTitle: settings.clinic?.pageTitle?.trim() || '',
          phone: (settings.clinic?.phone || []).map(p => p?.trim() || ''),
          address: (settings.clinic?.address || []).map(a => a?.trim() || ''),
          workingHours: (settings.clinic?.workingHours || []).map(w => w?.trim() || ''),
          clinicPhotos: settings.clinic?.clinicPhotos || [],
          images: settings.clinic?.images || [],
          socialMedia: {
            facebook: settings.clinic?.socialMedia?.facebook?.trim() || '',
            instagram: settings.clinic?.socialMedia?.instagram?.trim() || '',
            twitter: settings.clinic?.socialMedia?.twitter?.trim() || '',
            whatsapp: settings.clinic?.socialMedia?.whatsapp?.trim() || ''
          },
          contactInfo: {
            email: settings.clinic?.contactInfo?.email?.trim() || '',
            whatsapp: settings.clinic?.contactInfo?.whatsapp?.trim() || '',
            mapEmbed: settings.clinic?.contactInfo?.mapEmbed?.trim() || ''
          },
          location: {
            mapEmbed: settings.clinic?.location?.mapEmbed?.trim() || ''
          }
        },
        qualifications: settings.qualifications || []
      };

      // Save to Firestore
      await setDoc(doc(db, 'settings', 'website'), cleanSettings);
      
      toast({
        title: "تم الحفظ بنجاح",
        description: "تم تحديث إعدادات الموقع بنجاح",
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "خطأ في الحفظ",
        description: "حدث خطأ أثناء حفظ الإعدادات",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setSaving(true);
      await resetWebsiteSettings();
      await fetchSettings();
      toast({
        title: "تم إعادة التعيين بنجاح",
        description: "تم إعادة تعيين الإعدادات إلى القيم الافتراضية",
      });
    } catch (error) {
      console.error('Error resetting settings:', error);
      toast({
        title: "خطأ في إعادة التعيين",
        description: "حدث خطأ أثناء إعادة تعيين الإعدادات",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('key', IMGBB_API_KEY);

    const response = await fetch(IMGBB_API_URL, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload image');
    }

    const data = await response.json();
    return data.data.url;
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const imageUrl = await uploadImage(file);
      setSettings(prev => ({
        ...prev,
        clinic: {
          ...prev.clinic,
          clinicPhotos: [...prev.clinic.clinicPhotos, imageUrl]
        }
      }));
      toast({
        title: "تم رفع الصورة بنجاح",
        description: "تم إضافة الصورة إلى معرض العيادة",
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "خطأ في رفع الصورة",
        description: "حدث خطأ أثناء رفع الصورة",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageUrl: string) => {
    setSettings(prev => ({
      ...prev,
      clinic: {
        ...prev.clinic,
        clinicPhotos: prev.clinic.clinicPhotos.filter(photo => photo !== imageUrl)
      }
    }));
    toast({
      title: "تم حذف الصورة",
      description: "تم حذف الصورة من معرض العيادة",
    });
  };

  const addArrayItem = (section: 'clinic', field: 'phone' | 'address' | 'workingHours') => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: [...prev[section][field], '']
      }
    }));
  };

  const removeArrayItem = (section: 'clinic', field: 'phone' | 'address' | 'workingHours', index: number) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section][field].filter((_, i) => i !== index)
      }
    }));
  };

  const updateArrayItem = (section: 'clinic', field: 'phone' | 'address' | 'workingHours', index: number, value: string) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section][field].map((item, i) => i === index ? value : item)
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-blue-900">إعدادات الموقع</h1>
        <div className="space-x-4">
          <Button
            onClick={handleReset}
            variant="outline"
            className="flex items-center gap-2"
            disabled={saving}
          >
            <RefreshCw className="w-4 h-4" />
            إعادة التعيين
          </Button>
          <Button
            onClick={handleSave}
            className="flex items-center gap-2"
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Building2 className="w-4 h-4" />
            )}
            حفظ التغييرات
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Doctor Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              معلومات الدكتور
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>اسم الدكتور</Label>
              <Input
                value={settings.doctor.name}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  doctor: { ...prev.doctor, name: e.target.value }
                }))}
                placeholder="أدخل اسم الدكتور"
              />
            </div>
            <div className="space-y-2">
              <Label>المسمى الوظيفي</Label>
              <Input
                value={settings.doctor.title}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  doctor: { ...prev.doctor, title: e.target.value }
                }))}
                placeholder="أدخل المسمى الوظيفي"
              />
            </div>
            <div className="space-y-2">
              <Label>صورة الدكتور</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    try {
                      const imageUrl = await uploadImage(file);
                      setSettings(prev => ({
                        ...prev,
                        doctor: { ...prev.doctor, image: imageUrl }
                      }));
                    } catch (error) {
                      console.error('Error uploading image:', error);
                    }
                  }
                }}
              />
              {settings.doctor.image && (
                <div className="relative w-32 h-32">
                  <img
                    src={settings.doctor.image}
                    alt="Doctor"
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute -top-2 -right-2"
                    onClick={() => setSettings(prev => ({
                      ...prev,
                      doctor: { ...prev.doctor, image: '' }
                    }))}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>نبذة عن الدكتور</Label>
              <Textarea
                value={settings.doctor.about}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  doctor: { ...prev.doctor, about: e.target.value }
                }))}
                placeholder="أدخل نبذة عن الدكتور"
                rows={4}
              />
            </div>
            <div className="space-y-2">
              <Label>المؤهلات العلمية</Label>
              <div className="space-y-2">
                {settings.doctor.qualifications.map((q, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={q}
                      onChange={e => {
                        const newQuals = [...settings.doctor.qualifications];
                        newQuals[idx] = e.target.value;
                        setSettings(prev => ({
                          ...prev,
                          doctor: { ...prev.doctor, qualifications: newQuals }
                        }));
                      }}
                      placeholder="المؤهل العلمي"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => {
                        const newQuals = settings.doctor.qualifications.filter((_, i) => i !== idx);
                        setSettings(prev => ({
                          ...prev,
                          doctor: { ...prev.doctor, qualifications: newQuals }
                        }));
                      }}
                      disabled={settings.doctor.qualifications.length === 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSettings(prev => ({
                    ...prev,
                    doctor: { ...prev.doctor, qualifications: [...prev.doctor.qualifications, ''] }
                  }))}
                >
                  إضافة مؤهل جديد
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>سنوات الخبرة</Label>
                <Input
                  type="number"
                  value={settings.doctor.statistics.experienceYears === 0 ? '' : settings.doctor.statistics.experienceYears}
                  onChange={e => setSettings(prev => ({
                    ...prev,
                    doctor: {
                      ...prev.doctor,
                      statistics: {
                        ...prev.doctor.statistics,
                        experienceYears: e.target.value === '' ? 0 : parseInt(e.target.value)
                      }
                    }
                  }))}
                  placeholder="سنوات الخبرة"
                  min={0}
                />
              </div>
              <div className="space-y-2">
                <Label>الحالات الناجحة</Label>
                <Input
                  type="number"
                  value={settings.doctor.statistics.successfulCases === 0 ? '' : settings.doctor.statistics.successfulCases}
                  onChange={e => setSettings(prev => ({
                    ...prev,
                    doctor: {
                      ...prev.doctor,
                      statistics: {
                        ...prev.doctor.statistics,
                        successfulCases: e.target.value === '' ? 0 : parseInt(e.target.value)
                      }
                    }
                  }))}
                  placeholder="الحالات الناجحة"
                  min={0}
                />
              </div>
              <div className="space-y-2">
                <Label>نسبة رضا المرضى</Label>
                <Input
                  type="number"
                  value={settings.doctor.statistics.satisfactionRate === 0 ? '' : settings.doctor.statistics.satisfactionRate}
                  onChange={e => setSettings(prev => ({
                    ...prev,
                    doctor: {
                      ...prev.doctor,
                      statistics: {
                        ...prev.doctor.statistics,
                        satisfactionRate: e.target.value === '' ? 0 : parseInt(e.target.value)
                      }
                    }
                  }))}
                  placeholder="نسبة رضا المرضى"
                  min={0}
                  max={100}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clinic Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              معلومات العيادة
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>اسم العيادة</Label>
              <Input
                value={settings.clinic.name}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  clinic: { ...prev.clinic, name: e.target.value }
                }))}
                placeholder="أدخل اسم العيادة"
              />
            </div>
            <div className="space-y-2">
              <Label>شعار العيادة</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    try {
                      const imageUrl = await uploadImage(file);
                      setSettings(prev => ({
                        ...prev,
                        clinic: { ...prev.clinic, logo: imageUrl }
                      }));
                    } catch (error) {
                      console.error('Error uploading image:', error);
                    }
                  }
                }}
              />
              {settings.clinic.logo && (
                <div className="relative w-32 h-32">
                  <img
                    src={settings.clinic.logo}
                    alt="Clinic Logo"
                    className="w-full h-full object-contain rounded-lg"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute -top-2 -right-2"
                    onClick={() => setSettings(prev => ({
                      ...prev,
                      clinic: { ...prev.clinic, logo: '' }
                    }))}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>صورة الهيدر</Label>
              {settings.clinic.headerImage && (
                <div className="relative w-full h-48">
                  <img
                    src={settings.clinic.headerImage}
                    alt="Header"
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute -top-2 -right-2"
                    onClick={() => setSettings(prev => ({
                      ...prev,
                      clinic: { ...prev.clinic, headerImage: '' }
                    }))}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>عنوان الصفحة</Label>
              <Input
                value={settings.clinic.pageTitle}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  clinic: { ...prev.clinic, pageTitle: e.target.value }
                }))}
                placeholder="أدخل عنوان الصفحة"
              />
            </div>
            
            {/* Phone Numbers */}
            <div className="space-y-2">
              <Label>أرقام الهاتف</Label>
              <div className="space-y-2">
                {settings.clinic.phone.map((phone, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={phone}
                      onChange={e => updateArrayItem('clinic', 'phone', idx, e.target.value)}
                      placeholder="رقم الهاتف"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => removeArrayItem('clinic', 'phone', idx)}
                      disabled={settings.clinic.phone.length === 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addArrayItem('clinic', 'phone')}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  إضافة رقم جديد
                </Button>
              </div>
            </div>

            {/* Addresses */}
            <div className="space-y-2">
              <Label>العناوين</Label>
              <div className="space-y-2">
                {settings.clinic.address.map((address, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={address}
                      onChange={e => updateArrayItem('clinic', 'address', idx, e.target.value)}
                      placeholder="العنوان"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => removeArrayItem('clinic', 'address', idx)}
                      disabled={settings.clinic.address.length === 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addArrayItem('clinic', 'address')}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  إضافة عنوان جديد
                </Button>
              </div>
            </div>

            {/* Working Hours */}
            <div className="space-y-2">
              <Label>ساعات العمل</Label>
              <div className="space-y-2">
                {settings.clinic.workingHours.map((hours, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={hours}
                      onChange={e => updateArrayItem('clinic', 'workingHours', idx, e.target.value)}
                      placeholder="ساعات العمل"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => removeArrayItem('clinic', 'workingHours', idx)}
                      disabled={settings.clinic.workingHours.length === 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addArrayItem('clinic', 'workingHours')}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  إضافة ساعات عمل جديدة
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>صور العيادة</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-4">
                {settings.clinic.clinicPhotos.map((photo, index) => (
                  <div key={index} className="relative aspect-square">
                    <img
                      src={photo}
                      alt={`Clinic Photo ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2"
                      onClick={() => handleDeleteImage(photo)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Social Media */}
            <div className="space-y-2">
              <Label>روابط التواصل الاجتماعي</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>فيسبوك</Label>
                  <Input
                    value={settings.clinic.socialMedia.facebook}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      clinic: {
                        ...prev.clinic,
                        socialMedia: {
                          ...prev.clinic.socialMedia,
                          facebook: e.target.value
                        }
                      }
                    }))}
                    placeholder="رابط الفيسبوك"
                  />
                </div>
                <div>
                  <Label>انستجرام</Label>
                  <Input
                    value={settings.clinic.socialMedia.instagram}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      clinic: {
                        ...prev.clinic,
                        socialMedia: {
                          ...prev.clinic.socialMedia,
                          instagram: e.target.value
                        }
                      }
                    }))}
                    placeholder="رابط الانستجرام"
                  />
                </div>
                <div>
                  <Label>تويتر</Label>
                  <Input
                    value={settings.clinic.socialMedia.twitter}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      clinic: {
                        ...prev.clinic,
                        socialMedia: {
                          ...prev.clinic.socialMedia,
                          twitter: e.target.value
                        }
                      }
                    }))}
                    placeholder="رابط تويتر"
                  />
                </div>
                <div>
                  <Label>واتساب</Label>
                  <Input
                    value={settings.clinic.socialMedia.whatsapp}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      clinic: {
                        ...prev.clinic,
                        socialMedia: {
                          ...prev.clinic.socialMedia,
                          whatsapp: e.target.value
                        }
                      }
                    }))}
                    placeholder="رقم الواتساب"
                  />
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="space-y-2">
              <Label>كود الخريطة</Label>
              <Textarea
                value={settings.clinic.contactInfo.mapEmbed}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  clinic: {
                    ...prev.clinic,
                    contactInfo: {
                      ...prev.clinic.contactInfo,
                      mapEmbed: e.target.value
                    }
                  }
                }))}
                placeholder="أدخل كود الخريطة"
                rows={4}
              />
              {settings.clinic.contactInfo.mapEmbed && (
                <div className="mt-4">
                  <Label>معاينة الخريطة</Label>
                  <div 
                    className="w-full h-64 rounded-lg overflow-hidden border"
                    dangerouslySetInnerHTML={{ __html: settings.clinic.contactInfo.mapEmbed }}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Settings; 